import { useMemo } from 'react';
import { Activity } from 'lucide-react';
import { TimeSeriesChart } from '../charts/TimeSeriesChart';
import { formatCount } from '../../utils/logbook';
import type { LogbookAggregationResult } from '../../types/logbook';

interface LogbookActivityChartProps {
  aggregation: LogbookAggregationResult | null;
  loading?: boolean;
  /** Drag-to-zoom on the chart narrows the search window to the selection. */
  onBrush?: (startMs: number, endMs: number) => void;
  height?: number;
}

const BUCKET_LABELS: Record<number, string> = {
  60000: 'per minute',
  3600000: 'per hour',
  86400000: 'per day',
};

/**
 * When the matching evidence happened.
 *
 * The counts come from an aggregation, so every bar is the full matching set rather than the page
 * on screen: a spike is a real spike even when the results are paged. Dragging across the chart
 * narrows the window to the selection, which is how most investigations actually proceed, from a
 * wide sweep down to the ten minutes that matter.
 */
export const LogbookActivityChart = ({ aggregation, loading = false, onBrush, height = 200 }: LogbookActivityChartProps) => {
  const bucket = aggregation?.bucket_ms ?? 3600000;

  const series = useMemo(() => {
    if (!aggregation || aggregation.group_by !== 'time') return [];
    const points = aggregation.groups
      .map((group) => ({ t: Number(group.value), v: group.event_count }))
      .filter((point) => Number.isFinite(point.t))
      .sort((a, b) => a.t - b.t);
    if (points.length === 0) return [];
    return [{
      id: 'events',
      label: 'Events',
      points,
      color: 'var(--accent-300)',
      kind: 'area' as const,
      valueFormat: formatCount,
    }];
  }, [aggregation]);

  const total = useMemo(
    () => (aggregation?.groups ?? []).reduce((sum, group) => sum + group.event_count, 0),
    [aggregation],
  );

  return (
    <TimeSeriesChart
      series={series}
      height={height}
      isLoading={loading}
      zeroBased
      downsample="minmax"
      onBrush={onBrush}
      valueFormat={formatCount}
      icon={Activity}
      title={
        <span className="flex items-baseline gap-2">
          Activity
          <span className="text-xs font-normal text-glass-500 tabular-nums">
            {formatCount(total)} events {BUCKET_LABELS[bucket] ?? ''}
          </span>
        </span>
      }
      emptyMessage="No captured evidence in this window."
      ariaLabel="Captured events over time"
      bare
    />
  );
};
